import copy from "copy-to-clipboard";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { DropDown } from "@normasteaching/norma-ui";
import { Ellipsis } from "lucide-react";
import { SuccessMessage } from "../../UI/Success/SuccessMessage.tsx";
import { useMessageController } from "../../UI/useMessageController.ts";
import { NormasTeachingBundleDTO } from "../../ProductService/NormasTeachingBundle/useNormasTeachingBundleById.ts";
import { NormasTeachingCheckoutScreenPath } from "../../NormasTeachingCheckout/NormasTeachingCheckoutScreen.tsx";
import { useNavigate } from "react-router";
import { useState } from "react";
import BundleDetailsModal from "./NormasTeachingBundleDetailModal.tsx";
import { useDuplicateNormasTeachingBundle } from "../../ProductService/NormasTeachingBundle/useDuplicateNormasTeachingBundle.ts";
import { useSetNormasTeachingBundleEnabled } from "../../ProductService/NormasTeachingBundle/useSetNormasTeachingBundleEnabled.ts";
import { isItemDisabled } from "../../ProductService/isItemDisabled.ts";
import { DisabledBadge } from "../../UI/Admin/DisabledBadge.tsx";
import { Tooltip } from "../../UI/Tooltip.tsx";
import { mutate } from "swr";

const columnHelper = createColumnHelper<NormasTeachingBundleDTO>();
const columns = [
  columnHelper.accessor("_id", {
    header: "ID",
    cell: function IdColumn(info) {
      const { showMessage, message, open } = useMessageController();
      return (
        <>
          <SuccessMessage open={open} message={message} />
          <span
            className={"cursor-pointer"}
            onClick={() => {
              copy(info.getValue());
              showMessage({
                message: "ID del prodotto copiato negli appunti!",
                timeoutMs: 4 * 1000,
              });
            }}
          >
            {info.getValue()}
          </span>
        </>
      );
    },
    maxSize: 100,
  }),
  columnHelper.accessor("bundleName", {
    header: "Bundle Name",
    cell: (info) => {
      const fullName = `${info.row.original.bundleName} - ${info.row.original.variantBundleName}`;
      return (
        <span className="flex items-center gap-2 min-w-0">
          <Tooltip text={fullName}>
            <span className="cursor-default truncate">{fullName}</span>
          </Tooltip>
          {isItemDisabled(info.row.original) && <DisabledBadge />}
        </span>
      );
    },
    maxSize: 100,
  }),
  columnHelper.accessor("priceInCents", {
    header: "Prezzo",
    cell: (info) => {
      const v = info.getValue() ?? 0;
      return `${v.amount / 100} ${v.currency}`;
    },
    maxSize: 100,
  }),
  columnHelper.display({
    id: "actions",
    cell: function Actions(props) {
      const { showMessage, open, message } = useMessageController();
      const [selectedBundle, setSelectedBundle] =
        useState<NormasTeachingBundleDTO | null>(null);
      const navigate = useNavigate();
      const { duplicateNormasTeachingBundle, loading: duplicating } =
        useDuplicateNormasTeachingBundle();
      const { setNormasTeachingBundleEnabled, loading: togglingEnabled } =
        useSetNormasTeachingBundleEnabled();
      const disabled = isItemDisabled(props.row.original);

      function createProductUrl(): string {
        const url = new URL(
          NormasTeachingCheckoutScreenPath,
          window.location.origin,
        );
        url.searchParams.set("bundleId", props.row.original._id);
        return url.toString();
      }

      async function handleToggleEnabled() {
        const bundle = props.row.original;
        try {
          await setNormasTeachingBundleEnabled(bundle._id, disabled);
          // The list key carries the search term, so revalidate every
          // `/bundles` page rather than one exact key.
          await mutate(
            (key) => Array.isArray(key) && key[0] === "/bundles",
            undefined,
            { revalidate: true },
          );
          showMessage({
            message: `Bundle ${disabled ? "attivato" : "disattivato"} con successo!`,
            timeoutMs: 4 * 1000,
          });
        } catch (error) {
          showMessage({
            message: `Errore durante ${disabled ? "l'attivazione" : "la disattivazione"} del bundle`,
            timeoutMs: 4 * 1000,
          });
        }
      }

      async function handleDuplicateBundle() {
        const bundle = props.row.original;
        try {
          await duplicateNormasTeachingBundle(bundle._id);
          showMessage({
            message: "Bundle duplicato con successo!",
            timeoutMs: 4 * 1000,
          });
          window.location.reload();
        } catch (error) {
          showMessage({
            message: "Errore durante la duplicazione del bundle",
            timeoutMs: 4 * 1000,
          });
        }
      }

      return (
        <>
          <SuccessMessage open={open} message={message} />
          {selectedBundle && (
            <BundleDetailsModal
              bundle={selectedBundle}
              onClose={() => setSelectedBundle(null)}
            />
          )}

          <DropDown activator={<Ellipsis className={"w-5"} />}>
            <div className={""}>
              <div
                className={
                  "p-3 hover:bg-primary hover:text-white cursor-pointer"
                }
                onClick={() => {
                  setSelectedBundle(props.row.original);
                }}
              >
                <span>Visualizza bundle</span>
              </div>
              <div
                className={
                  "p-3 hover:bg-primary hover:text-white cursor-pointer"
                }
                onClick={() => {
                  navigate(
                    `/admin/normasteaching/edit-bundle/${props.row.original._id}`,
                  );
                }}
              >
                <span>Modifica bundle</span>
              </div>
              <div
                className={
                  "p-3 hover:bg-primary hover:text-white cursor-pointer"
                }
                onClick={handleDuplicateBundle}
                style={{ opacity: duplicating ? 0.5 : 1 }}
              >
                <span>
                  {duplicating ? "Duplicazione..." : "Duplica bundle"}
                </span>
              </div>
              <div
                className={
                  "p-3 hover:bg-primary hover:text-white cursor-pointer"
                }
                onClick={handleToggleEnabled}
                style={{ opacity: togglingEnabled ? 0.5 : 1 }}
              >
                <span>{disabled ? "Attiva bundle" : "Disattiva bundle"}</span>
              </div>

              <div
                className={
                  "p-3 hover:bg-primary hover:text-white cursor-pointer"
                }
                onClick={() => {
                  copy(createProductUrl());
                  showMessage({
                    message: "URL del prodotto copiato negli appunti!",
                    timeoutMs: 4 * 1000,
                  });
                }}
              >
                <span>Copia URL</span>
              </div>
              <div
                className={
                  "p-3 hover:bg-primary hover:text-white cursor-pointer"
                }
                onClick={() => {
                  window.open(
                    createProductUrl(),
                    "_blank",
                    "noopener,noreferrer",
                  );
                }}
              >
                <span>Vedi Checkout</span>
              </div>
            </div>
          </DropDown>
        </>
      );
    },
  }),
];
export function AdminNormasTeachingBundleTable({
  bundles,
}: {
  bundles: NormasTeachingBundleDTO[];
}) {
  const [selectedBundle, setSelectedBundle] =
    useState<NormasTeachingBundleDTO | null>(null);

  const table = useReactTable({
    data: bundles,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      {selectedBundle && (
        <BundleDetailsModal
          bundle={selectedBundle}
          onClose={() => setSelectedBundle(null)}
        />
      )}
      <table
        className={
          "w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400"
        }
        style={{ position: "relative", zIndex: 1 }}
      >
        <thead
          className={
            "text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400"
          }
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <tr className={""} key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th className={"px-6 py-4"} key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              className={
                "bg-white border-b dark:bg-gray-800 dark:border-gray-700 border-gray-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600"
              }
              key={row.id}
              onClick={() => setSelectedBundle(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  style={{
                    maxWidth: cell.column.columnDef.maxSize
                      ? cell.column.columnDef.maxSize
                      : "none",
                    overflow:
                      cell.column.id !== "actions" ? "hidden" : undefined,
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    position:
                      cell.column.id === "bundleName" ? "relative" : undefined,
                  }}
                  className={"px-6 py-4"}
                  key={cell.id}
                  onClick={
                    cell.column.id === "actions"
                      ? (e) => e.stopPropagation()
                      : undefined
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          {table.getFooterGroups().map((footerGroup) => (
            <tr key={footerGroup.id}>
              {footerGroup.headers.map((header) => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.footer,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </tfoot>
      </table>
    </>
  );
}
